import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { trackAnalyticsEvent } from '../../../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../../../services/eventTaxonomy';
import { Box, IconButton } from '@mui/material';

import TimelineControl from './TimelineControl';
import TravelTimeChart from './TravelTimeChart';
import UnmatchedRidesList from './UnmatchedRidesList';
import type { UnmatchedRide } from '../../../../types/simulation';
import OperationsView from './OperationsView';
import type { VehicleOperation } from './useVehicleOperations';
import type { RideSchema, SimulationActivity, SimulationRunSummary } from '../../../../types/simulation';

import { Icon } from '@liftango/ui-icons';
const TARMAC = '#3A3A48';
const ORANGE = '#EE7334';
const FONT_FAMILY = '"Roboto", sans-serif';
const TAB_BAR_HEIGHT = 40;
export const ANALYTICS_DECK_CONTENT_HEIGHT_DEFAULT = 208;
const CONTENT_HEIGHT_MIN = 180;
const CONTENT_HEIGHT_MAX = 520;
const RESIZE_HANDLE_HEIGHT = 6;
/** Resize handle + tab strip (tabs, collapse chevron). Must fit when content is collapsed. */
export const ANALYTICS_DECK_CHROME_HEIGHT = RESIZE_HANDLE_HEIGHT + TAB_BAR_HEIGHT;

export type AnalyticsDeckTab = 'timeline' | 'travel_time' | 'unmatched' | 'operations';

export interface AnalyticsDeckProps {
  ridePoints: RideSchema[] | null | undefined;
  currentTime: Date | null;
  onTimeChange: (time: Date) => void;
  windowMinutes: number;
  onWindowMinutesChange: (minutes: number) => void;
  unmatchedRides?: UnmatchedRide[] | null;
  activities?: SimulationActivity[] | null;
  /** Run ID (e.g. for CSV download filename) */
  runId?: string | null;
  /** Selected simulation summary from results API for stats export. */
  statsSummary?: SimulationRunSummary | null;
  /** When provided (e.g. page variant with map), clicking an activity in the driver detail panel highlights it on the map and centers the map */
  onHighlightActivity?: (activity: SimulationActivity | null) => void;
  /** When provided (e.g. page variant), clicking a vehicle in Operations opens the detail panel (e.g. right-side); pass setState to control the panel */
  onVehicleSelectForDetailPanel?: (vehicle: VehicleOperation | null) => void;
  vehicleVisibilityMap?: Record<string, boolean>;
  onVehicleVisibilityChange?: (vehicleId: string, visible: boolean) => void;
  onShowAllVehicles?: () => void;
  onHideAllVehicles?: () => void;
  /** Full-page map: stable key of focused unallocated ride (Operations ↔ map). */
  selectedUnmatchedRideKey?: string | null;
  /** Full-page map: focus an unallocated ride on the map (from Operations timeline). */
  onSelectUnmatchedRide?: (ride: UnmatchedRide) => void;
  /** Show unallocated trips on the map and Operations timeline (toggled from Operations). */
  unallocatedVisible?: boolean;
  onUnallocatedVisibleChange?: (visible: boolean) => void;
  /** Called whenever the deck's total rendered height changes (collapsed or expanded + resized). */
  onDeckHeightChange?: (totalHeight: number) => void;
  selectedSim?: SimulationRunSummary | null;
  simulationOptions?: SimulationRunSummary[];
}

const AnalyticsDeck: React.FC<AnalyticsDeckProps> = ({
  ridePoints,
  currentTime,
  onTimeChange,
  windowMinutes,
  onWindowMinutesChange,
  unmatchedRides,
  activities,
  runId,
  statsSummary,
  onHighlightActivity,
  onVehicleSelectForDetailPanel,
  vehicleVisibilityMap,
  onVehicleVisibilityChange,
  onShowAllVehicles,
  onHideAllVehicles,
  selectedUnmatchedRideKey = null,
  onSelectUnmatchedRide,
  unallocatedVisible = true,
  onUnallocatedVisibleChange,
  onDeckHeightChange,
  selectedSim = null,
  simulationOptions = [],
}) => {
  const { t } = useTranslation('simulation');
  const TABS = useMemo<{ id: AnalyticsDeckTab; label: string }[]>(
    () => [
      { id: 'operations', label: t('results.analyticsDeck.tabOperations') },
      { id: 'timeline', label: t('results.analyticsDeck.tabTimeline') },
      { id: 'travel_time', label: t('results.analyticsDeck.tabTravelDistance') },
      { id: 'unmatched', label: t('results.analyticsDeck.tabUnmatched') },
    ],
    [t]
  );
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<AnalyticsDeckTab>('operations');
  const [contentHeight, setContentHeight] = useState(ANALYTICS_DECK_CONTENT_HEIGHT_DEFAULT);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartY = useRef(0);
  const resizeStartHeight = useRef(ANALYTICS_DECK_CONTENT_HEIGHT_DEFAULT);
  /** Once Operations is available, select it once so the tab stays the default after loading. */
  const hasAppliedOperationsDefaultRef = useRef(false);
  const prevTabRef = useRef<AnalyticsDeckTab | null>(null);

  const lastWindowMinutesRef = useRef<number>(windowMinutes);

  const handleWindowMinutesChange = useCallback(
    (next: number) => {
      if (next !== lastWindowMinutesRef.current) {
        lastWindowMinutesRef.current = next;
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_timeline_window_changed, {
          window_hours: Number((next / 60).toFixed(2)),
          timestamp: new Date().toISOString(),
        });
      }
      onWindowMinutesChange(next);
    },
    [onWindowMinutesChange]
  );

  const handleUnallocatedVisibleChange = useCallback(
    (visible: boolean) => {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_unallocated_visibility_changed, {
        is_visible: visible,
        timestamp: new Date().toISOString(),
      });
      onUnallocatedVisibleChange?.(visible);
    },
    [onUnallocatedVisibleChange]
  );

  const handleVehicleVisibilityChange = useCallback(
    (vehicleId: string, visible: boolean) => {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_vehicle_visibility_toggled, {
        is_visible: visible,
        timestamp: new Date().toISOString(),
      });
      onVehicleVisibilityChange?.(vehicleId, visible);
    },
    [onVehicleVisibilityChange]
  );

  const handleTabChange = useCallback((tabId: AnalyticsDeckTab) => {
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_tab_changed, {
      tab_name: tabId,
      previous_tab: prevTabRef.current,
      timestamp: new Date().toISOString(),
    });
    prevTabRef.current = tabId;
    setActiveTab(tabId);
  }, []);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = contentHeight;
  }, [contentHeight]);

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: MouseEvent) => {
      const delta = resizeStartY.current - e.clientY;
      const next = Math.max(
        CONTENT_HEIGHT_MIN,
        Math.min(CONTENT_HEIGHT_MAX, resizeStartHeight.current + delta)
      );
      setContentHeight(next);
    };
    const onUp = () => setIsResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizing]);

  useEffect(() => {
    const totalHeight = isExpanded ? ANALYTICS_DECK_CHROME_HEIGHT + contentHeight : ANALYTICS_DECK_CHROME_HEIGHT;
    onDeckHeightChange?.(totalHeight);
  }, [isExpanded, contentHeight, onDeckHeightChange]);

  const points = ridePoints ?? [];
  const hasTravelDurationData = useMemo(
    () => points.some((p) => typeof (p as RideSchema & { duration_minutes?: number }).duration_minutes === 'number'),
    [points]
  );
  const hasUnmatchedData = unmatchedRides != null;
  const hasOperationsData =
    (activities != null && activities.length > 0) ||
    (unmatchedRides != null && unmatchedRides.length > 0);

  const visibleTabs = useMemo(
    () =>
      TABS.filter(
        (tab) =>
          (tab.id !== 'travel_time' || hasTravelDurationData) &&
          (tab.id !== 'unmatched' || hasUnmatchedData) &&
          (tab.id !== 'operations' || hasOperationsData)
      ),
    [hasTravelDurationData, hasUnmatchedData, hasOperationsData, TABS]
  );

  const visibleTabIds = useMemo(() => new Set(visibleTabs.map((t) => t.id)), [visibleTabs]);

  useEffect(() => {
    if (!visibleTabIds.has(activeTab)) {
      const fallback = visibleTabs[0]?.id ?? 'timeline';
      setActiveTab(fallback);
    }
  }, [activeTab, visibleTabIds, visibleTabs]);

  useEffect(() => {
    if (hasAppliedOperationsDefaultRef.current) return;
    if (!visibleTabIds.has('operations')) return;
    hasAppliedOperationsDefaultRef.current = true;
    setActiveTab('operations');
  }, [visibleTabIds]);

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 20,
        backgroundColor: TARMAC,
        fontFamily: FONT_FAMILY,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        transition: isResizing ? 'none' : 'height 0.3s ease, min-height 0.3s ease',
        height: isExpanded ? ANALYTICS_DECK_CHROME_HEIGHT + contentHeight : ANALYTICS_DECK_CHROME_HEIGHT,
        minHeight: ANALYTICS_DECK_CHROME_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Resize handle: drag to change deck height */}
      <Box
        onMouseDown={handleResizeMouseDown}
        sx={{
          height: RESIZE_HANDLE_HEIGHT,
          minHeight: RESIZE_HANDLE_HEIGHT,
          cursor: 'ns-resize',
          backgroundColor: 'rgba(0,0,0,0.04)',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
          flexShrink: 0,
        }}
      />
      {/* Tab bar: tabs left, centered collapse chevron, map path mode right (full-page map only) */}
      <Box
        sx={{
          height: TAB_BAR_HEIGHT,
          minHeight: TAB_BAR_HEIGHT,
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'stretch',
          columnGap: 0.5,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          flexShrink: 0,
          backgroundColor: TARMAC,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.2)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start', minWidth: 0, overflow: 'hidden' }}>
          {visibleTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <Box
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                sx={{
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: isActive ? `2px solid ${ORANGE}` : '2px solid transparent',
                  color: isActive ? '#fff' : '#94A3B8',
                  fontFamily: FONT_FAMILY,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  flexShrink: 0,
                  '&:hover': { color: '#fff' },
                }}
              >
                {tab.label}
              </Box>
            );
          })}
        </Box>
        <IconButton
          size="small"
          onClick={() => setIsExpanded((e) => !e)}
          aria-label={isExpanded ? t('results.analyticsDeck.collapseAria') : t('results.analyticsDeck.expandAria')}
          sx={{
            alignSelf: 'center',
            color: '#fff',
            borderRadius: 1,
            justifySelf: 'center',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
          }}
        >
          {isExpanded ? (
            <Icon size={26} name="chevron-down" />
          ) : (
            <Icon size={26} name="chevron-up" />
          )}
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexShrink: 0,
            pr: 1.5,
            pl: 0.5,
            gap: 0.5,
            minWidth: 0,
          }}
        >
        </Box>
      </Box>

      {/* Content area - explicit height so it scales with resize; stays fixed to bottom via parent */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          height: isExpanded ? contentHeight : 0,
          overflow: 'hidden',
          transition: 'height 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {activeTab === 'timeline' && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <TimelineControl
            ridePoints={points}
            activities={activities}
            currentTime={currentTime}
            onTimeChange={onTimeChange}
            windowMinutes={windowMinutes}
            onWindowMinutesChange={handleWindowMinutesChange}
            embedded
          />
          </Box>
        )}
        {activeTab === 'travel_time' && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <TravelTimeChart data={points} />
          </Box>
        )}
        {activeTab === 'unmatched' && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <UnmatchedRidesList
              data={unmatchedRides}
              selectedUnmatchedRideKey={selectedUnmatchedRideKey}
              onSelectRide={onSelectUnmatchedRide}
            />
          </Box>
        )}
        {activeTab === 'operations' && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <OperationsView
              activities={activities ?? null}
              ridePoints={ridePoints ?? null}
              unmatchedRides={unmatchedRides ?? null}
              runId={runId}
              statsSummary={statsSummary ?? null}
              onHighlightActivity={onHighlightActivity}
              onVehicleSelectForDetailPanel={onVehicleSelectForDetailPanel}
              vehicleVisibilityMap={vehicleVisibilityMap}
              onVehicleVisibilityChange={handleVehicleVisibilityChange}
              onShowAllVehicles={onShowAllVehicles}
              onHideAllVehicles={onHideAllVehicles}
              selectedUnmatchedRideKey={selectedUnmatchedRideKey}
              onSelectUnmatchedRide={onSelectUnmatchedRide}
              unallocatedVisible={unallocatedVisible}
              onUnallocatedVisibleChange={handleUnallocatedVisibleChange}
              selectedSim={selectedSim}
              simulationOptions={simulationOptions}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AnalyticsDeck;
